import * as React from "react";

import { Alert, MenuItem, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { UseFormRegister, FormState } from "react-hook-form";

import { MyOrgContext } from "context/myorgcontext";
import Loader from "src/common/components/Loader";
import { schedulerDisplayName } from "src/common/textAlteration";
import { Org } from "src/domain/administrative/config-management/api/getOrg";
import { useGetBlueprintSchedulers, useGetMySchedulers } from "src/domain/core/workflow/api/getSchedulers";

const LoadingContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const Row = styled("div")(() => ({
  marginBottom: "30px",
  display: "flex",
  justifyContent: "space-between",
}));

const SmallerRow = styled("div")(() => ({
  marginBottom: "5px",
  display: "flex",
  justifyContent: "space-between",
}));

interface SchedulerInputProps {
  setUserSchedulersError: React.Dispatch<React.SetStateAction<Error>>;
  setBlueprintSchedulersError: React.Dispatch<React.SetStateAction<Error>>;
  setInitializeExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  template: Template;
  blueprintId: number;
  register: UseFormRegister<any>;
  formState: FormState<any>;
}

const loadingState = () => {
  return (
    <LoadingContainer>
      <div key="loader">
        <Loader />
      </div>
    </LoadingContainer>
  );
};

export const SchedulerInput: React.FC<SchedulerInputProps> = ({
  setUserSchedulersError,
  setBlueprintSchedulersError,
  setInitializeExpanded,
  template,
  blueprintId,
  register,
  formState,
}) => {
  const myOrg: Org = React.useContext(MyOrgContext);

  const { data: userSchedulers, error: userSchedulersError } = useGetMySchedulers();
  const { data: blueprintSchedulers, error: blueprintSchedulersError } = useGetBlueprintSchedulers(blueprintId);

  React.useEffect(() => {
    if (userSchedulersError) {
      setUserSchedulersError(userSchedulersError);
    }
    if (blueprintSchedulersError) {
      setBlueprintSchedulersError(blueprintSchedulersError);
    }
  }, [userSchedulersError, blueprintSchedulersError, setUserSchedulersError, setBlueprintSchedulersError]);

  const eligibleSchedulers: Scheduler[] = React.useMemo(() => {
    if (!blueprintSchedulers || !userSchedulers) {
      return;
    }

    return blueprintSchedulers?.filter((bs) => {
      return !!userSchedulers?.find((us) => us?.id === bs?.id);
    });
  }, [blueprintSchedulers, userSchedulers]);

  React.useEffect(() => {
    if (setInitializeExpanded && eligibleSchedulers) {
      setInitializeExpanded(!(eligibleSchedulers?.length > 0));
    }
  }, [eligibleSchedulers, setInitializeExpanded]);

  if (!userSchedulers || !blueprintSchedulers) {
    return loadingState();
  }

  const getInitialScheduler = (): number => {
    // No Eligible Schedulers, return early
    if (!eligibleSchedulers || eligibleSchedulers.length === 0) {
      return -1;
    }

    // Workflow Template Launch
    if (template && template.body?.schedulerId) {
      const templateSchedulerAvailable =
        eligibleSchedulers.findIndex((es) => es.id === template.body.schedulerId) !== -1;

      if (templateSchedulerAvailable) {
        return template.body.schedulerId;
      }
    }

    // Non-Template Launch
    const defaultSchedulerIsEligible =
      eligibleSchedulers.findIndex((es) => es.id === myOrg.data.default_scheduler_id) !== -1;

    if (defaultSchedulerIsEligible) {
      return myOrg.data.default_scheduler_id;
    }

    // No Template or Default scheduler available
    return eligibleSchedulers[0].id;
  };

  const initialSchedulerIdSelection = getInitialScheduler();

  const schedulersAvailable = initialSchedulerIdSelection > 0;

  return (
    <>
      <SmallerRow>
        {!schedulersAvailable && (
          <Alert severity="error">
            You are not approved to use any of the schedulers available on this blueprint. You cannot create a workflow
            without a scheduler. Please contact your organization administrator for assistance.
          </Alert>
        )}
      </SmallerRow>
      <Row>
        <TextField
          select
          id="scheduler_id"
          variant="outlined"
          inputProps={{
            "data-testid": "scheduler",
          }}
          label="Job Scheduler *"
          helperText={formState.errors?.scheduler_id?.message || "Please select a job scheduler for your workflow"}
          fullWidth
          defaultValue={initialSchedulerIdSelection >= 0 ? initialSchedulerIdSelection : ""}
          {...register("scheduler_id")}
          error={!!formState.errors?.scheduler_id}
          disabled={!schedulersAvailable}
        >
          {eligibleSchedulers?.map((option) => {
            return (
              <MenuItem key={option.id} value={option.id}>
                <Typography variant="body2">{schedulerDisplayName(option.name)}</Typography>
              </MenuItem>
            );
          })}
        </TextField>
      </Row>
    </>
  );
};
